import React, { useEffect, useMemo, useState } from 'react';
import { getUsers, getInvites, createInvite, deleteInvite, deleteUser } from '../services/api';
import { useTimezone } from '../context/TimezoneContext';
import { Invite, User } from '../types';
import { copyToClipboard } from '../utils/copyToClipboard';

const ROLES = [
  { value: 'viewer', label: 'Viewer', detail: 'Read-only dashboard access' },
  { value: 'operator', label: 'Operator', detail: 'Acknowledge alerts and operate basic workflows' },
  { value: 'engineer', label: 'Engineer', detail: 'Manage devices, parsers, and configurations' },
  { value: 'admin', label: 'Admin', detail: 'Full tenant access and governance control' },
];

interface TeamProps {
  user: User;
}

export default function Team({ user }: TeamProps) {
  const { formatTime } = useTimezone();
  const [members, setMembers] = useState<User[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const isAdmin = user.role === 'admin';

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [usersData, invitesData] = await Promise.all([getUsers(), getInvites()]);
      setMembers(usersData);
      setInvites(invitesData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load team data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!success && !error) return;
    const timer = setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3500);
    return () => clearTimeout(timer);
  }, [success, error]);

  const roleCounts = useMemo(
    () =>
      members.reduce((acc, member) => {
        acc[member.role] = (acc[member.role] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    [members]
  );
  const selectedRole = ROLES.find((item) => item.value === role);
  const pendingInvites = invites.filter((invite) => !invite.accepted_at);
  const expiredInvites = pendingInvites.filter((invite) => new Date(invite.expires_at).getTime() < Date.now()).length;

  async function handleInvite(event: React.FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    setInviteLoading(true);
    setError('');
    setSuccess('');
    try {
      const invite = await createInvite({ email: email.trim(), role });
      setEmail('');
      setRole('viewer');
      if (invite.invite_url) {
        setSuccess(`Invite created. Link sent to ${invite.email} if SMTP is configured; otherwise copy the link below.`);
      } else {
        setSuccess(`Invite created for ${invite.email}.`);
      }
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send invite.');
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleRevoke(inviteId: string) {
    if (!window.confirm('Revoke this invite?')) return;
    setError('');
    try {
      await deleteInvite(inviteId);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke invite.');
    }
  }

  async function handleDeleteMember(member: User) {
    if (!window.confirm(`Delete ${member.email} from this tenant?`)) return;
    setError('');
    setSuccess('');
    try {
      await deleteUser(member.id);
      setSuccess(`${member.email} was removed from the tenant.`);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete member.');
    }
  }

  async function copyLink(invite: Invite) {
    if (!invite.invite_url) return;
    const ok = await copyToClipboard(invite.invite_url);
    if (ok) {
      setCopiedInviteId(invite.id);
      setTimeout(() => setCopiedInviteId(null), 2000);
    }
  }

  return (
    <section className="sd-page-stack sd-team-page">
      <div className="sd-page-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">ORGANIZATION</div>
          <h1>Team Management</h1>
          <p>Control tenant access, invite collaborators, and review pending seats.</p>
        </div>
        <div className="sd-page-actions">
          <button className="sd-secondary-button" onClick={loadData} type="button">
            Refresh
          </button>
        </div>
      </div>

      {(success || error) && (
        <div className="sd-team-message-stack">
          {success && <div className="sd-toast is-success">{success}</div>}
          {error && <div className="sd-toast is-error">{error}</div>}
        </div>
      )}

      <section className="sd-kpi-grid" aria-label="Team metrics">
        <article className="sd-kpi-card">
          <div className="sd-kpi-label">MEMBERS</div>
          <div className="sd-kpi-value">{members.length}</div>
          <div className="sd-kpi-detail">{roleCounts.admin || 0} admin{(roleCounts.admin || 0) === 1 ? '' : 's'}</div>
          <div className="sd-kpi-footer"><span>Tenant users</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-warning">
          <div className="sd-kpi-label">PENDING INVITES</div>
          <div className="sd-kpi-value">{pendingInvites.length}</div>
          <div className="sd-kpi-detail">{expiredInvites} expired</div>
          <div className="sd-kpi-footer"><span>Awaiting acceptance</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-success">
          <div className="sd-kpi-label">OPERATORS</div>
          <div className="sd-kpi-value">{roleCounts.operator || 0}</div>
          <div className="sd-kpi-detail">{roleCounts.engineer || 0} engineers</div>
          <div className="sd-kpi-footer"><span>Field access</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-blue">
          <div className="sd-kpi-label">CURRENT ROLE</div>
          <div className="sd-kpi-value sd-team-role-value">{user.role}</div>
          <div className="sd-kpi-detail">{user.email}</div>
          <div className="sd-kpi-footer"><span>{isAdmin ? 'Admin tools enabled' : 'Read-only access'}</span></div>
        </article>
      </section>

      <section className="sd-team-workspace">
        {isAdmin && (
          <aside className="sd-panel sd-team-invite-panel">
            <div className="sd-panel-header">
              <div>
                <h2>Invite Console</h2>
                <p>Create secure invite links for tenant users.</p>
              </div>
            </div>
            <form className="sd-team-invite-form" onSubmit={handleInvite}>
              <label className="sd-field">
                <span>Email</span>
                <input
                  placeholder="e.g. colleague@company.com"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <small>Work email that will receive the tenant invite link.</small>
              </label>
              <label className="sd-field">
                <span>Role</span>
                <select value={role} onChange={(event) => setRole(event.target.value)}>
                  {ROLES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label} - {item.detail}
                    </option>
                  ))}
                </select>
                <small>Controls what this teammate can view or manage inside the tenant.</small>
              </label>
              <div className="sd-team-role-preview">
                <strong>{selectedRole?.label}</strong>
                <span>{selectedRole?.detail}</span>
              </div>
              <button className="sd-primary-button" disabled={inviteLoading} type="submit">
                {inviteLoading ? 'Sending...' : 'Send Invite'}
              </button>
            </form>
          </aside>
        )}

        <article className="sd-panel sd-team-table-panel">
          <div className="sd-panel-header">
            <div>
              <h2>Pending Invites</h2>
              <p>Copy invitation URLs or revoke unused access.</p>
            </div>
          </div>
          <div className="sd-table-wrap sd-team-invite-table-wrap">
            <table className="sd-table sd-team-invite-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Expires</th>
                  <th>Invited By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.length === 0 ? (
                  <tr><td className="sd-empty-row" colSpan={5}>No pending invites.</td></tr>
                ) : (
                  pendingInvites.map((invite) => (
                    <tr key={invite.id}>
                      <td><strong>{invite.email}</strong></td>
                      <td><RoleBadge role={invite.role} /></td>
                      <td>{formatTime(invite.expires_at)}</td>
                      <td>{invite.invited_by_email || '-'}</td>
                      <td>
                        <div className="sd-row-actions">
                          {invite.invite_url && (
                            <button onClick={() => copyLink(invite)} type="button">
                              {copiedInviteId === invite.id ? 'Copied' : 'Copy Link'}
                            </button>
                          )}
                          {isAdmin && (
                            <button className="is-danger" onClick={() => handleRevoke(invite.id)} type="button">
                              Revoke
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="sd-team-invite-card-list sd-mobile-record-list">
            {pendingInvites.length === 0 ? (
              <div className="sd-mobile-empty-card">No pending invites.</div>
            ) : (
              pendingInvites.map((invite) => (
                <article className="sd-mobile-record-card" key={invite.id}>
                  <header className="sd-mobile-record-header">
                    <RoleBadge role={invite.role} />
                    <time>{formatTime(invite.expires_at)}</time>
                  </header>
                  <div className="sd-mobile-record-title">
                    <strong>{invite.email}</strong>
                    <span>Invited by {invite.invited_by_email || '-'}</span>
                  </div>
                  <div className="sd-mobile-record-actions">
                    {invite.invite_url && (
                      <button onClick={() => copyLink(invite)} type="button">
                        {copiedInviteId === invite.id ? 'Copied' : 'Copy Link'}
                      </button>
                    )}
                    {isAdmin && (
                      <button className="is-danger" onClick={() => handleRevoke(invite.id)} type="button">
                        Revoke
                      </button>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        </article>
      </section>

      <article className="sd-panel sd-team-members-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Current Members</h2>
            <p>Review tenant users and remove accounts that no longer need access.</p>
          </div>
        </div>
        <div className="sd-table-wrap sd-team-members-table-wrap">
          <table className="sd-table sd-team-members-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Tenant</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="sd-empty-row" colSpan={5}>Loading team members...</td></tr>
              ) : members.length === 0 ? (
                <tr><td className="sd-empty-row" colSpan={5}>No members found.</td></tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <strong>{member.email}</strong>
                      {member.id === user.id && <span>Signed-in account</span>}
                    </td>
                    <td><RoleBadge role={member.role} /></td>
                    <td>{member.created_at ? formatTime(member.created_at) : '-'}</td>
                    <td><code>{member.tenant_id}</code></td>
                    <td>
                      {member.id === user.id ? (
                        <span className="sd-team-self-label">You</span>
                      ) : isAdmin ? (
                        <div className="sd-row-actions">
                          <button className="is-danger" onClick={() => handleDeleteMember(member)} type="button">
                            Delete
                          </button>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="sd-team-member-card-list sd-mobile-record-list">
          {loading ? (
            <div className="sd-mobile-empty-card">Loading team members...</div>
          ) : members.length === 0 ? (
            <div className="sd-mobile-empty-card">No members found.</div>
          ) : (
            members.map((member) => (
              <article className="sd-mobile-record-card" key={member.id}>
                <header className="sd-mobile-record-header">
                  <RoleBadge role={member.role} />
                  {member.id === user.id && <span className="sd-team-self-label">You</span>}
                </header>
                <div className="sd-mobile-record-title">
                  <strong>{member.email}</strong>
                  <span>{member.created_at ? `Joined ${formatTime(member.created_at)}` : 'Join date unavailable'}</span>
                </div>
                <div className="sd-mobile-record-fact">
                  <span>Tenant</span>
                  <code>{member.tenant_id}</code>
                </div>
                {member.id !== user.id && isAdmin && (
                  <div className="sd-mobile-record-actions">
                    <button className="is-danger" onClick={() => handleDeleteMember(member)} type="button">
                      Delete
                    </button>
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </article>
    </section>
  );
}

function RoleBadge({ role }: { role: string }) {
  const tone = role === 'admin' ? 'is-admin' : role === 'engineer' ? 'is-engineer' : role === 'operator' ? 'is-operator' : 'is-viewer';
  return (
    <span className={`sd-team-role-badge ${tone}`}>
      <i />
      {role}
    </span>
  );
}
